import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
} from '@tanstack/react-query';
import { MutationFetchFunction } from 'common/helpers/common';
import { ApiError, ApiResult } from 'common/repositories/common.model';

import {
  HotelSaleModel,
  HotelSaleMutationInput,
  HotelSaleDeleteInput,
  HotelSaleUpdateInput,
  HotelSaleReschedulePutInput,
} from './model';

export function useCreateHotelSale(
  options?: UseMutationOptions<
    ApiResult<HotelSaleModel>,
    ApiError,
    HotelSaleMutationInput
  >,
): UseMutationResult<
  ApiResult<HotelSaleModel>,
  ApiError,
  HotelSaleMutationInput
> {
  return useMutation<
    ApiResult<HotelSaleModel>,
    ApiError,
    HotelSaleMutationInput
  >(async function (body) {
    return await MutationFetchFunction({
      url: 'hotel-sales',
      method: 'POST',
      classType: HotelSaleModel,
      body,
    });
  }, options);
}

export function useUpdateHotelSale(
  options?: UseMutationOptions<
    ApiResult<HotelSaleModel>,
    ApiError,
    HotelSaleUpdateInput
  >,
): UseMutationResult<
  ApiResult<HotelSaleModel>,
  ApiError,
  HotelSaleUpdateInput
> {
  return useMutation<ApiResult<HotelSaleModel>, ApiError, HotelSaleUpdateInput>(
    async function (body) {
      return await MutationFetchFunction({
        url: `hotel-sales/${body.hotelSaleId}`,
        method: 'PUT',
        classType: HotelSaleModel,
        body: body.body,
      });
    },
    options,
  );
}

export function useRescheduleHotelSale(
  options?: UseMutationOptions<
    ApiResult<HotelSaleModel>,
    ApiError,
    HotelSaleReschedulePutInput
  >,
): UseMutationResult<
  ApiResult<HotelSaleModel>,
  ApiError,
  HotelSaleReschedulePutInput
> {
  return useMutation<
    ApiResult<HotelSaleModel>,
    ApiError,
    HotelSaleReschedulePutInput
  >(async function (body) {
    return await MutationFetchFunction({
      url: `hotel-sales/${body.hotelSaleId}/reschedule`,
      method: 'PUT',
      body: body.body,
    });
  }, options);
}

export function useDeleteHotelSale(
  options?: UseMutationOptions<
    { message: string },
    ApiError,
    HotelSaleDeleteInput
  >,
): UseMutationResult<{ message: string }, ApiError, HotelSaleDeleteInput> {
  return useMutation<{ message: string }, ApiError, HotelSaleDeleteInput>(
    async function (body) {
      return await MutationFetchFunction({
        url: `hotel-sales/${body.id}`,
        method: 'DELETE',
      });
    },
    options,
  );
}

export function useVoidHotelSale(
  options?: UseMutationOptions<
    { message: string },
    ApiError,
    HotelSaleDeleteInput
  >,
): UseMutationResult<{ message: string }, ApiError, HotelSaleDeleteInput> {
  return useMutation<{ message: string }, ApiError, HotelSaleDeleteInput>(
    async function (body) {
      return await MutationFetchFunction({
        url: `hotel-sales/${body.id}/void`,
        method: 'PATCH',
      });
    },
    options,
  );
}

export function useCompleteHotelSale(
  options?: UseMutationOptions<
    { message: string },
    ApiError,
    HotelSaleDeleteInput
  >,
): UseMutationResult<{ message: string }, ApiError, HotelSaleDeleteInput> {
  return useMutation<{ message: string }, ApiError, HotelSaleDeleteInput>(
    async function (body) {
      return await MutationFetchFunction({
        url: `hotel-sales/${body.id}/complete`,
        method: 'PATCH',
      });
    },
    options,
  );
}

export function useRevertCompleteHotelSale(
  options?: UseMutationOptions<
    { message: string },
    ApiError,
    HotelSaleDeleteInput
  >,
): UseMutationResult<{ message: string }, ApiError, HotelSaleDeleteInput> {
  return useMutation<{ message: string }, ApiError, HotelSaleDeleteInput>(
    async function (body) {
      return await MutationFetchFunction({
        url: `hotel-sales/${body.id}/revert-complete`,
        method: 'PATCH',
      });
    },
    options,
  );
}
